import React, { useState, useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import { LangContext } from '../../context/lang-context'
import useFetch from '../../hooks/fetchMSSQL-hook'
import NoResults from '../../components/NoResults/NoResults'
import { NavigationContext } from '../../context/navigation-context'
import SearchFilter from "../../components/Filter/SearchFilter"
import SufficiencyChartModal from '../../components/Modals/SufficiencyChartModal'

const DEFAULT_SUFFICIENCY_LIMIT_PERCENT = 50;

const parseSufficiencyData = (value) => {
  if (!value) return null;

  if (typeof value === "object") {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (err) {
    console.error("Nie udało się sparsować danych wystarczalności", err);
    return null;
  }
};

const getSufficiencyLimitPercent = (data, entry) => {
  const rawLimitPercent =
    entry?.SufficiencyLimitPercent
    ?? data?.SufficiencyLimitPercent
    ?? data?.SufficiencyThreshold
    ?? data?.TotalSufficiencyThreshold
    ?? data?.Threshold
    ?? data?.TotalSufficiency?.Threshold;
  const numericLimitPercent = Number(rawLimitPercent);

  return Number.isFinite(numericLimitPercent)
    ? numericLimitPercent
    : DEFAULT_SUFFICIENCY_LIMIT_PERCENT;
};

const getSufficiencyBadgeClass = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "badge-secondary";
  }

  const text = String(value).trim();

  if (text.toLowerCase() === "brak danych") return "badge-secondary";
  if (text.startsWith(">")) return "badge-info";
  if (/^0\s*h$/i.test(text)) return "badge-danger";
  if (/^\d+\s*h$/i.test(text)) return Number.parseInt(text, 10) <= 24 ? "badge-warning" : "badge-success";
  if (/^1\s*(?:dzień|d)(?:\s*0\s*h)?$/i.test(text)) return "badge-warning";
  if (/(?:^|\s)(?:dni|dzień|d)(?:\s|$)/i.test(text)) return "badge-success";

  const numericValue = Number(text);
  if (Number.isFinite(numericValue)) {
    if (numericValue <= 0) return "badge-danger";
    if (numericValue <= 24) return "badge-warning";
    return "badge-success";
  }

  return "badge-secondary";
};

const normalizeINMEntry = (entry) => {
  const Sufficiency = parseSufficiencyData(entry?.Sufficiency);
  const SufficiencyLimitPercent = getSufficiencyLimitPercent(Sufficiency, entry);

  return {
    ...entry,
    Sufficiency,
    SufficiencyLimitPercent,
  };
};

const INMS = () => {
  const { setHeaderData } = useContext(NavigationContext);
  const { TRL_Pack } = useContext(LangContext);
  const { fetchMssqlApi } = useFetch();

  const [INMS, setINMS] = useState([]);
  const [sufficiencyMachine, setSufficiencyMachine] = useState(null);
  const [loadingSufficiencyMachineId, setLoadingSufficiencyMachineId] = useState(null);
  const [recalculatingMachineId, setRecalculatingMachineId] = useState(null);

  const reportList = [{ reportId: 46, reportColor: "danger" }];

  const defaultFilter = {
    name: "INMS",
    page: 1,
    rowsPerPage: 50,
    sortBy: "none",
    machineId: null,
    machineName: "",
    userId: null,
    userName: "",
    machineTags: { id: [], name: [] },
  };

  const [filter, setFilter] = useState(defaultFilter);
  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 50,
  });

  const fetchSufficiency = (machineId) => new Promise((resolve, reject) => {
    fetchMssqlApi(
      `inms/${machineId}/sufficiency`,
      { method: "GET", hideNotification: true },
      (response) => {
        resolve(Array.isArray(response) ? response[0] : response);
      },
      reject
    );
  });

  const openSufficiencyChart = async (entry) => {
    const machineId = Number(entry?.MachineId);
    if (!Number.isSafeInteger(machineId) || machineId <= 0) return;
    if (loadingSufficiencyMachineId !== null) return;

    setLoadingSufficiencyMachineId(machineId);
    try {
      const response = await fetchSufficiency(machineId);
      if (!response || Number(response.MachineId) !== machineId) {
        throw new Error("Nieprawidłowa odpowiedź serwera");
      }

      const Sufficiency = parseSufficiencyData(response.Sufficiency);
      if (!Array.isArray(Sufficiency?.Series?.Data) || Sufficiency.Series.Data.length === 0) {
        window.alert("Brak danych o wystarczalności dla tego automatu.");
        return;
      }

      setSufficiencyMachine({ ...entry, Sufficiency, SufficiencyUpdateDateTime: response.SufficiencyUpdateDateTime });
    } catch (err) {
      console.error("Nie udało się pobrać wykresu wystarczalności", err);
      window.alert("Nie udało się pobrać wykresu wystarczalności.");
    } finally {
      setLoadingSufficiencyMachineId(null);
    }
  };

  const recalculateSufficiency = (entry) => {
    const machineId = Number(entry?.MachineId);

    if (!Number.isFinite(machineId)) {
      return Promise.resolve();
    }

    setRecalculatingMachineId(machineId);

    return new Promise((resolve, reject) => {
      fetchMssqlApi(
        "inms",
        {
          method: "POST",
          hideNotification: true,
          data: {
            machineIds: JSON.stringify([machineId]),
            createUserId: null,
            pageSize: 1,
            pageNo: 1,
            sortBy: "none",
          },
        },
        async (response) => {
          const rows = Array.isArray(response)
            ? response
            : Array.isArray(response?.rows)
              ? response.rows
              : [];

          const refreshedEntry = rows
            .map(normalizeINMEntry)
            .find((row) => Number(row?.MachineId) === machineId);

          let Sufficiency;
          let SufficiencyUpdateDateTime;
          try {
            const details = await fetchSufficiency(machineId);
            if (!details || Number(details.MachineId) !== machineId) {
              throw new Error("Nieprawidłowa odpowiedź serwera");
            }
            Sufficiency = parseSufficiencyData(details.Sufficiency);
            SufficiencyUpdateDateTime = details.SufficiencyUpdateDateTime;
          } catch (err) {
            console.error("Nie udało się ponownie pobrać danych wystarczalności", err);
            setRecalculatingMachineId(null);
            reject(err);
            return;
          }

          if (refreshedEntry) {
            setINMS((prev) =>
              prev.map((row) =>
                Number(row?.MachineId) === machineId
                  ? { ...row, ...refreshedEntry }
                  : row
              )
            );
          }

          setSufficiencyMachine((prev) =>
            Number(prev?.MachineId) === machineId
              ? { ...prev, ...(refreshedEntry || {}), Sufficiency, SufficiencyUpdateDateTime }
              : prev
          );

          setRecalculatingMachineId(null);
          resolve(refreshedEntry ? { ...refreshedEntry, Sufficiency, SufficiencyUpdateDateTime } : { ...entry, Sufficiency, SufficiencyUpdateDateTime });
        },
        (err) => {
          console.error("Nie udało się ponownie pobrać danych wystarczalności", err);
          setRecalculatingMachineId(null);
          reject(err);
        }
      );
    });
  };

  const getMachineIdsByTags = (machineTagIds) => {
    return new Promise((resolve, reject) => {
      fetchMssqlApi(
        "tags/getMachineIdsByTags",
        { method: "POST", hideNotification: true, data: { machineTagIds } },
        (response) => {
          const ids = Array.isArray(response)
            ? response.map(Number).filter(Number.isFinite)
            : [];

          resolve(ids);
        },
        (err) => reject(err)
      );
    });
  };

  const search = async (f = defaultFilter, pageSize, page) => {
    const activeFilter = { ...defaultFilter, ...(f || {}) };
    const rowsPerPage = Math.max(
      1,
      Number(pageSize ?? settingsFilter?.rowsPerPage ?? defaultFilter.rowsPerPage) || 50
    );
    const pageNo = Math.max(1, Number(page ?? 1) || 1);

    try {
      let machineIds = [];

      if (activeFilter?.machineTags?.id?.length > 0) {
        machineIds = await getMachineIdsByTags(activeFilter.machineTags.id);
      }

      if (activeFilter.machineId !== null && activeFilter.machineId !== undefined) {
        const machineId = Number(activeFilter.machineId);
        if (Number.isFinite(machineId)) machineIds.push(machineId);
      }

      machineIds = [...new Set(machineIds)];

      fetchMssqlApi(
        "inms",
        {
          method: "POST",
          hideNotification: true,
          data: {
            machineIds: JSON.stringify(machineIds),
            createUserId:
              activeFilter.userId !== null && activeFilter.userId !== undefined
                ? Number(activeFilter.userId)
                : null,
            pageSize: rowsPerPage,
            pageNo,
            sortBy: activeFilter.sortBy || "none",
          },
        },
        (response) => {
          const rows = Array.isArray(response)
            ? response
            : Array.isArray(response?.rows)
              ? response.rows
              : [];

          setINMS(rows.map(normalizeINMEntry));
        }
      );
    } catch (err) {
      console.error("Nie udało się pobrać stanów bieżących", err);
      setINMS([]);
    }
  };

  const sortDefaults = {
    CreateDateTime: "DESC",
    MachineFullName: "ASC",
    StatePercent: "DESC",
    DOCreateDateTime: "DESC",
    SalesAfterLoadQuantity: "DESC",
    CashSalesAfterKPValue: "DESC",
    KPCreateDateTime: "DESC",
  };

  const getSortState = () => {
    const [field, dir] = String(filter.sortBy || "").split("|");
    return { field, dir: (dir || "").toUpperCase() };
  };

  const sortState = getSortState();

  const arrow = (field) => {
    if (sortState.field !== field) return "";
    return sortState.dir === "ASC" ? " ▲" : " ▼";
  };

  const handleHeaderSort = (field) => {
    const [curField, curDirRaw] = String(filter.sortBy || "").split("|");
    const curDir = (curDirRaw || "").toUpperCase();

    let nextDir;
    if (curField === field) {
      nextDir = curDir === "ASC" ? "DESC" : "ASC";
    } else {
      nextDir = sortDefaults[field] || "ASC";
    }

    const next = { ...filter, sortBy: `${field}|${nextDir}`, page: 1 };
    setFilter(next);
    search(next, null, 1);
  };

  useEffect(() => {
    setHeaderData({ text: TRL_Pack.inm.inms });
  }, []);

  return (
    <div>
      <SearchFilter
        filterName="INMS"
        searchFilter={filter}
        defaultFilter={defaultFilter}
        reportList={reportList}
        onFilterChange={(f) => setFilter((prev) => ({ ...prev, ...f }))}
        onSettingsChange={(s) => setSettingsFilter((prev) => ({ ...prev, ...s }))}
        search={search}
        totalCount={INMS.length}
        forNow
      />

      <div className="overflow-auto border custom-scroll" style={{ maxHeight: "calc(100vh - 150px)" }}>
        {INMS.length ? (
          <table className="table table-striped">
            <thead>
              <tr className="headers-color text-center headers-stick-trxs">
                <th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleHeaderSort("CreateDateTime")}
                >
                  Czas zapisu, Źródło{arrow("CreateDateTime")}
                </th>

                <th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleHeaderSort("MachineFullName")}
                >
                  Automat{arrow("MachineFullName")}
                </th>

                <th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleHeaderSort("StatePercent")}
                >
                  Stan bieżący{arrow("StatePercent")}
                </th>

                <th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleHeaderSort("DOCreateDateTime")}
                >
                  Ostatnie doładowanie{arrow("DOCreateDateTime")}
                </th>

                <th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleHeaderSort("SalesAfterLoadQuantity")}
                >
                  Sprzedaż bieżąca{arrow("SalesAfterLoadQuantity")}
                </th>

                <th>
                  Wystarczalność całkowita /<br />najlepszego produktu
                </th>

                <th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleHeaderSort("CashSalesAfterKPValue")}
                >
                  Sprzedaż gotówkowa od <br />ostatniego rozliczenia monet{arrow("CashSalesAfterKPValue")}
                </th>

                <th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleHeaderSort("KPCreateDateTime")}
                >
                  Ostatnie rozliczenie monet{arrow("KPCreateDateTime")}
                </th>
              </tr>
            </thead>

            <tbody>
              {INMS.map((entry, idx) => {
                const totalSufficiency = entry.TotalSufficiency;
                const productSufficiency = entry.ProductSufficiency;
                const sufficiencyLimitPercent = entry.SufficiencyLimitPercent ?? DEFAULT_SUFFICIENCY_LIMIT_PERCENT;
                const hasTotalSufficiency = totalSufficiency !== null && totalSufficiency !== undefined && String(totalSufficiency).trim() !== "";
                const hasProductSufficiency = productSufficiency !== null && productSufficiency !== undefined && String(productSufficiency).trim() !== "";

                return (
                  <tr key={idx} className="text-center">
                    <td className="p-1">
                      <div>{entry.Created}</div>
                    </td>

                    <td className="p-1">
                      <Link to={`/machine/${entry.MachineId}`} className="btn btn-sm btn-outline-dark">
                        {entry.MachineFullName}
                      </Link>
                    </td>

                    <td className="p-1">
                      {entry.MachineInventoryId !== null && (
                        <Link
                          to={`/inm/${entry.MachineInventoryId}`}
                          className="btn text-nowrap btn-sm btn-outline-danger"
                        >
                          {entry.INm ? entry.INm : entry.MachineInventoryId}
                        </Link>
                      )}
                      <br />
                      <div className="pt-1">
                        {entry.StateAndCapacity} ({entry.StatePercent}%)
                      </div>
                    </td>

                    <td className="p-1">
                      {entry.LoadMachineInventoryId !== null && (
                        <Link
                          to={`/do/${entry.LoadMachineInventoryId}`}
                          className="btn text-nowrap btn-sm btn-outline-info"
                        >
                          {entry.DO}
                        </Link>
                      )}
                      <br />
                      <div className="pt-1">{entry.DOCreateDateTime}</div>
                    </td>

                    <td className="p-1">
                      {entry.SalesAfterLoadQuantity !== null && `${entry.SalesAfterLoadQuantity} szt. `}
                      {entry.DepositSalesAfterLoadQuantity !== null &&
                        `(+${entry.DepositSalesAfterLoadQuantity} kaucji)`}
                    </td>

                    <td className="p-1 align-middle" style={{ width: "230px" }}>
                      <div className="d-flex align-items-center justify-content-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center mr-2"
                          style={{
                            width: "30px",
                            height: "30px",
                            padding: 0,
                            flexShrink: 0,
                          }}
                          onClick={() => openSufficiencyChart(entry)}
                          disabled={loadingSufficiencyMachineId !== null}
                          title="Pokaż wykres wystarczalności"
                        >
                          <i className={loadingSufficiencyMachineId === Number(entry.MachineId)
                            ? "fas fa-spinner fa-spin"
                            : "fas fa-chart-line"} />
                        </button>

                        <div
                          className="d-flex flex-column align-items-center justify-content-center"
                          style={{ minWidth: "105px", gap: "4px" }}
                        >
                          {hasTotalSufficiency && (
                            <span
                              className={`badge ${getSufficiencyBadgeClass(totalSufficiency)} px-2 py-1 text-center`}
                              title="Wystarczalność całkowita"
                            >
                              {totalSufficiency} &gt; {sufficiencyLimitPercent}
                            </span>
                          )}

                          {hasProductSufficiency && (
                            <span
                              className={`badge ${getSufficiencyBadgeClass(productSufficiency)} px-2 py-1 text-center`}
                              title="Wystarczalność najlepszego produktu"
                            >
                              {productSufficiency}
                            </span>
                          )}

                          {!hasTotalSufficiency && !hasProductSufficiency && (
                            <span className="badge badge-secondary px-2 py-1 text-center">
                              Brak danych
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="p-1">
                      {entry.CashSalesAfterKPValue !== null
                        ? String(entry.CashSalesAfterKPValue).replace('.', ',')
                        : '-'}{' '}
                      zł
                    </td>

                    <td className="p-1">
                      {entry.MachineCoinInventoryId !== null && (
                        <button className="btn text-nowrap btn-sm btn-outline-success">
                          {entry.KP}
                        </button>
                      )}
                      <br />
                      <div className="pt-1">{entry.KPCreateDateTime}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <NoResults />
        )}
      </div>

      <SufficiencyChartModal
        machine={sufficiencyMachine}
        handleClose={() => setSufficiencyMachine(null)}
        onRecalculate={recalculateSufficiency}
        recalculating={
          Number(recalculatingMachineId) === Number(sufficiencyMachine?.MachineId)
        }
      />
    </div>
  );
};

export default INMS;